import { useAppContext } from 'providers/AppProvider';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { useAppSelector } from 'hooks';
import axios from 'axios';

import EmailLayout from 'layouts/EmailLayout';
import Avatar from 'components/base/Avatar';
import Button from 'components/base/Button';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';

import { Card, Col, Row, Dropdown, Collapse, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import TicketTodoCard from './TicketTodoCard';


interface ApiMessage {
  id?: string | number;
  message: string;
  avatar_url?: string;
  date_created: string;
  sender_name?: string;
  sender_email?: string;
  owner_type?: string;  // "customer" | "agent"
}

interface ApiTicket {
  id?: string | number;
  title?: string;
  name?: string;
  email?: string;
  country?: string;      // e.g., "US", "RO"
  os?: string;           // e.g., "Windows 11"
  browser?: string;      // e.g., "Chrome 124"
  current_url?: string;  // page where ticket was created
  priority?: string;     // "low" | "medium" | "high" | "urgent"
  status?: string;       // "open" | "pending" | ...
  ticket_type?: string;  // "general" | "billing" | ...
  group?: string;        // team/group name or id
  assigned_to?: string;  // agent id or "unassigned"
}



const ViewTicket = () => {
  const [messageText, setMessageText] = useState<string>('');
  const { token } = useAppSelector(state => state.auth);

  const {
    config: { isDark }
  } = useAppContext();

  const { ticketId } = useParams();

  const [ticket, setTicket] = useState<ApiTicket | null>(null);
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openState, setOpenState] = useState<boolean[]>([]);

  // Sidebar controls
  const [priority, setPriority] = useState<string>('low');
  const [status, setStatus] = useState<string>('open');
  const [ticketType, setTicketType] = useState<string>('general');
  const [group, setGroup] = useState<string>('unassigned');
  const [assignedAgent, setAssignedAgent] = useState<string>('unassigned');

  // Example option lists — replace with API-driven lists if you have endpoints
  const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
  const STATUS_OPTIONS = ['open', 'pending', 'on_hold', 'resolved', 'closed'];
  const TYPE_OPTIONS = ['general', 'billing', 'technical', 'sales', 'other'];
  const GROUP_OPTIONS = ['unassigned', 'support', 'billing', 'sales', 'success', 'engineering'];
  // You can fill this from your /agents endpoint
  const AGENT_OPTIONS = [
    { id: 'unassigned', name: 'Unassigned' },
    // { id: 'agent_123', name: 'Jane Doe' },
    // { id: 'agent_456', name: 'John Smith' }
  ];

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const stripHtmlTags = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    return date.toLocaleTimeString('en-US', options);
  };

  const replaceBrWithNewLine = (message: string): string => {
    return message.replace(/<br\s*\/?>/gi, '');
  };

  const linkify = (message: string): string => {
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    return message.replace(urlPattern, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
  };

  const processMessage = (message: string): string => {
    const withNewLines = replaceBrWithNewLine(message);
    return linkify(withNewLines);
  };

  const toggleOpen = (index: number) => {
    setOpenState((prev) => prev.map((isOpen, i) => (i === index ? !isOpen : isOpen)));
  };

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://api20.easychatdesk.com/get-ticket-details/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setTicket(response.data.ticket);
      setMessages(response.data.messages);

      // Initialize sidebar selects from ticket data (fallbacks added)
      const t = response.data.ticket || {};
      setPriority(t.priority || 'low');
      setStatus(t.status || 'open');
      setTicketType(t.ticket_type || 'general');
      setGroup(t.group || 'unassigned');
      setAssignedAgent(t.assigned_to || 'unassigned');

      const messagesLength = response.data.messages.length;
      const initialState = Array(messagesLength).fill(false);

      if (messagesLength >= 1) initialState[messagesLength - 1] = true;
      if (messagesLength >= 2) initialState[messagesLength - 2] = true;

      setOpenState(initialState);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch ticket details.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;

    try {
      const resp = await axios.post(
        'https://api20.easychatdesk.com/send-ticket-reply',
        {
          ticket_id: ticketId,
          message: messageText
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: false
        }
      );

      console.log('Success:', resp.data);
      setMessageText('');
      fetchTicketDetails();
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        console.error('Unauthorized: please log in again.');
      } else {
        console.error('Error sending message:', err);
      }
    }
  };

  // Generic update helper for sidebar fields
  const updateTicketField = async (field: string, value: string) => {
    if (!ticketId) return;
    try {
      // 🔧 Adjust this endpoint/payload to match your API
      await axios.patch(
        `https://api20.easychatdesk.com/tickets/${ticketId}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      // Optimistic update
      setTicket((prev) => (prev ? ({ ...prev, [field]: value } as ApiTicket) : prev));
    } catch (e) {
      console.error(`Failed updating ${field}`, e);
    }
  };

  // Handle sidebar select changes (updates API immediately)
  const onPriorityChange = (v: string) => {
    setPriority(v);
    updateTicketField('priority', v);
  };
  const onStatusChange = (v: string) => {
    setStatus(v);
    updateTicketField('status', v);
  };
  const onTicketTypeChange = (v: string) => {
    setTicketType(v);
    updateTicketField('ticket_type', v);
  };
  const onGroupChange = (v: string) => {
    setGroup(v);
    updateTicketField('group', v);
  };
  const onAgentChange = (v: string) => {
    setAssignedAgent(v);
    updateTicketField('assigned_to', v);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!ticket) return <p>Ticket not found.</p>;



  const getInitial = (name?: string) => (name?.trim()?.charAt(0)?.toUpperCase() || 'U');

const MessageAvatar = ({ msg }: { msg: ApiMessage }) => {
  // show real avatar only for agent messages when URL is present
  if (msg.owner_type === 'agent' && msg.avatar_url) {
    // If your <Avatar> supports a `src` prop, use that:
    return <Avatar size="xl" src={"https://api20.easychatdesk.com" + msg.avatar_url} />;

    // If it doesn't, uncomment this <img> fallback and remove the line above:
    // return (
    //   <img
    //     src={msg.avatar_url}
    //     alt={msg.sender_name || 'Agent'}
    //     style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
    //   />
    // );
  }

  // Fallback: initials bubble
  return (
    <Avatar size="xl" variant="name">
      {getInitial(msg.sender_name)}
    </Avatar>
  );
};



  return (
    <EmailLayout page="detail">
      <Row className="g-3">
        {/* Main thread column */}
        <Col md={8}>
          <Col md={12} className="mb-3">
            <Card className="email-content123">
              <Card.Header className="border-0">
                <div className="d-flex flex-between-center pb-3 border-bottom border-translucent">
                  <Button as={Link} to="/tickets" variant="link" className="p-0 text-body-secondary me-3">
                    <FontAwesomeIcon icon={faAngleLeft} className="fw-bolder fs-8" />
                  </Button>
                  <h3 className="flex-1 mb-0 lh-sm line-clamp-1">
                    {ticket?.title}
                  </h3>

                  <RevealDropdownTrigger>
                    <RevealDropdown>
                      <Dropdown.Item>Edit</Dropdown.Item>
                      <Dropdown.Item className="text-danger">Delete</Dropdown.Item>
                      <Dropdown.Item>Download</Dropdown.Item>
                      <Dropdown.Item>Report abuse</Dropdown.Item>
                    </RevealDropdown>
                  </RevealDropdownTrigger>
                </div>
              </Card.Header>
              <Card.Body className="p-0 pb-4 scrollbar">
                <div className="email-detail-content px-4">
                  <Row className="align-items-center gy-3 gx-2 mb-5">
                    <Col xs="auto">
                      <Avatar size="xl" variant="name">
                        {ticket?.name?.charAt(0) || 'U'}
                      </Avatar>
                    </Col>
                    <Col xs="auto" className="flex-1">
                      <div className="d-flex mb-1">
                        <h5 className="mb-0 text-body-highlight me-2">
                          {ticket?.name || 'Unknown Customer'}
                        </h5>
                        <p className="mb-0 lh-sm text-body-tertiary fs-9 d-none d-md-block text-nowrap">
                          &lt; {ticket?.email} &gt;
                        </p>
                      </div>
                      {messages[0] && (
                        <p className="mb-0 fs-9">
                          <span className="text-body-highlight fw-semibold fs-10 me-1">
                            {formatDate(messages[0].date_created)}
                          </span>
                          <span className="fw-semibold text-body fs-10 me-1">
                            {formatTime(messages[0].date_created)}
                          </span>
                          <FontAwesomeIcon icon={farStar} className="text-body-quaternary" />
                        </p>
                      )}
                    </Col>
                  </Row>

                  <div className="text-body-highlight fs-8 w-100 w-md-75 mb-2">
                    {messages[0]?.message}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {messages.slice(1).map((item: any, index: number) => (
            <Col md={12} className="mb-3" key={index}>
              <Card className="email-content123">
                <Card.Header onClick={() => toggleOpen(index)} className="cursor-pointer">
                  <Row className="align-items-center gy-3 gx-2">
                    <Col xs="auto">
                     <Col xs="auto">
    <MessageAvatar msg={item} />
  </Col>
                    </Col>
                    <Col xs="auto" className="flex-1">
                      <div className="d-flex mb-1">
                        <h5 className="mb-0 text-body-highlight me-2">{item.sender_name || 'Unknown'}</h5>
                        <p className="mb-0 lh-sm text-body-tertiary fs-9 d-none d-md-block text-nowrap">
                          &lt; {item?.sender_email} &gt;
                        </p>
                      </div>
                      <p className="mb-0 fs-9">
                        <span className="text-body-highlight fw-semibold fs-10 me-1">
                          {formatDate(item.date_created)}
                        </span>
                        <span className="fw-semibold text-body fs-10 me-1">
                          {formatTime(item.date_created)}
                        </span>
                        <FontAwesomeIcon icon={farStar} className="text-body-quaternary" />
                      </p>
                    </Col>
                  </Row>
                </Card.Header>

                <Card.Body className="p-0 scrollbar">
                  <div className="email-detail-content p-4">
                    {!openState[index] && (
                      <p
                        onClick={() => toggleOpen(index)}
                        className="message-content mb-0 pb-0"
                        style={{ cursor: 'pointer', whiteSpace: 'pre-line', color: isDark ? '#444' : '#ccc' }}
                      >
                        {stripHtmlTags((item.message || '').split('\n')[0] || '')}
                      </p>
                    )}
                    <Collapse in={openState[index]}>
                      <div className="mt-2">
                        <p
                          className="message-content"
                          style={{ whiteSpace: 'pre-line' }}
                          dangerouslySetInnerHTML={{ __html: processMessage(item.message || '') }}
                        />
                      </div>
                    </Collapse>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}

          <Col md={12} className="mb-3">
            <Card className="p-3">
              <div className="message-box">
                <Form.Group>
                  <Form.Label>Reply to the customer</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    placeholder="Type your message here..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                  />
                </Form.Group>
                <Button variant="primary" onClick={handleSendMessage} className="mt-2">
                  Send
                </Button>
              </div>
            </Card>
          </Col>
        </Col>

        {/* Sidebar column */}
        <Col md={4}>
          {/* Customer Details */}
          <Card className="mb-3">
            <Card.Header>
              <strong>Customer details</strong>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">Name</div>
                <div className="fw-semibold">{ticket?.name || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">Email</div>
                <div className="fw-semibold">{ticket?.email || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">Country</div>
                <div className="fw-semibold">{ticket?.country || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">OS</div>
                <div className="fw-semibold">{ticket?.os || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">Browser</div>
                <div className="fw-semibold">{ticket?.browser || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-body-secondary fs-9">Page where created</div>
                {ticket?.current_url ? (
                  <a href={ticket.current_url} target="_blank" rel="noreferrer" className="text-decoration-underline">
                    {ticket.current_url}
                  </a>
                ) : (
                  <div className="fw-semibold">—</div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Ticket Settings */}
          <Card className='mb-3'>
            <Card.Header>
              <strong>Ticket settings</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={priority}
                  onChange={(e) => onPriorityChange(e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => onStatusChange(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.replace('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase())}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Ticket type</Form.Label>
                <Form.Select
                  value={ticketType}
                  onChange={(e) => onTicketTypeChange(e.target.value)}
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Group</Form.Label>
                <Form.Select
                  value={group}
                  onChange={(e) => onGroupChange(e.target.value)}
                >
                  {GROUP_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Assigned agent</Form.Label>
                <Form.Select
                  value={assignedAgent}
                  onChange={(e) => onAgentChange(e.target.value)}
                >
                  {AGENT_OPTIONS.map((ag) => (
                    <option key={ag.id} value={ag.id}>
                      {ag.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>

          <TicketTodoCard
  className="mb-3"
  ticketId={ticketId as string}
  token={token ?? ""}
/>

        </Col>
      </Row>
    </EmailLayout>
  );
};

export default ViewTicket;
